import { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * Lightweight sortable table.
 * columns: [{ key, header, align?, render?(row), sortValue?(row) }]
 */
export function SortableTable({ columns, rows, initialSort, emptyText = 'Nessun dato.' }) {
  const [sort, setSort] = useState(initialSort || { key: columns[0]?.key, dir: 'desc' });

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const getVal = col.sortValue || ((r) => r[col.key]);
    return [...rows].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va ?? '').localeCompare(String(vb ?? ''));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const toggle = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  if (!rows.length) return <p className="text-sm text-ink-muted">{emptyText}</p>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted ${col.align === 'right' ? 'text-right' : 'text-left'}`}
              >
                <button type="button" onClick={() => toggle(col.key)} className={`inline-flex items-center gap-1 hover:text-ink ${col.align === 'right' ? 'flex-row-reverse' : ''}`}>
                  {col.header}
                  {sort.key === col.key && (sort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sorted.map((row, idx) => (
            <tr key={row._key || row.id || idx}>
              {columns.map((col) => (
                <td key={col.key} className={`px-3 py-2 align-middle ${col.align === 'right' ? 'text-right tabular-nums' : 'text-left'} text-ink`}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
